import { AuthConnection, KeybanUser } from "@keyban/sdk-base";
import {
  PasswordlessLoginInput,
  PasswordlessStartInput,
  PasswordLoginInput,
} from "@keyban/sdk-base/rpc";
import React from "react";

import { useKeybanClient } from "~/provider";

/**
 * Represents the authentication state of the user.
 *
 * This type can be one of the following states:
 *
 * - **Unintialized**:
 *   - `user`: `undefined`
 *   - `isAuthenticated`: `undefined`
 *
 * - **Unauthenticated**:
 *   - `user`: `null`
 *   - `isAuthenticated`: `false`
 *
 * - **Authenticated**:
 *   - `user`: `KeybanUser`
 *   - `isAuthenticated`: `true`
 */
export type BaseAuth =
  | { user: undefined; isAuthenticated: undefined }
  | { user: null; isAuthenticated: false }
  | { user: KeybanUser; isAuthenticated: true };

/**
 * Represents the authentication context, extending the base authentication state
 * with loading status and methods for various login/logout flows.
 * @property isLoading - Indicates whether an authentication operation is currently in progress.
 * @property login - Initiates the login process, optionally specifying a connection type.
 * @property passwordLogin - Initiates login using username and password.
 * @property passwordlessStart - Initiates the passwordless login flow by sending an OTP.
 * @property passwordlessLogin - Completes the passwordless login flow using the received OTP.
 */
export type AuthContext = BaseAuth & {
  /**
   * Indicates whether an authentication operation is currently in progress.
   * True while any login, logout or passwordless flow is pending.
   */
  isLoading: boolean;

  /**
   * Initiates the login process.
   * @param connection - Optional authentication connection type (e.g. "email", "sms").
   * @returns A promise that resolves when login completes.
   * @throws {Error} If login fails (network error or invalid credentials).
   */
  login: (connection?: AuthConnection) => Promise<void>;

  /**
   * Logs out the current user.
   * @returns A promise that resolves when logout completes.
   * @throws {Error} If logout fails.
   */
  logout: () => Promise<void>;

  /**
   * Initiates login using username and password.
   * @param input -
   * @returns A promise that resolves when authentication succeeds.
   * @throws {Error} If credentials are invalid or login fails.
   */
  passwordLogin(input: PasswordLoginInput): Promise<void>;

  /**
   * Initiates the passwordless login flow by sending an OTP.
   * @param input -
   * @returns A promise that resolves when the OTP is sent.
   * @throws {Error} If sending OTP fails (invalid username or rate limit).
   */
  passwordlessStart(input: PasswordlessStartInput): Promise<void>;

  /**
   * Completes the passwordless login flow using the received OTP.
   * @param input -
   * @returns A promise that resolves when authentication succeeds.
   * @throws {Error} If the phone number or OTP is invalid or expired.
   *   - On invalid input: "Wrong phone number or verification code."
   *   - Backend may return other codes (e.g. expired OTP)
   */
  passwordlessLogin(input: PasswordlessLoginInput): Promise<void>;
};

const KeybanAuthContext: React.Context<AuthContext | null> =
  React.createContext<AuthContext | null>(null);

/**
 * KeybanAuthProvider
 * @param props - Component props
 * @param props.children - Children
 * @returns - JSX element
 * @private
 */
export function KeybanAuthProvider({ children }: React.PropsWithChildren) {
  const client = useKeybanClient();

  const [user, setUser] = React.useState<KeybanUser | null>();
  const [pendingUpdates, setPendingUpdates] = React.useState(0);

  const wrapUpdate = React.useCallback(
    async (cb?: () => Promise<unknown>) => {
      try {
        setPendingUpdates((count) => count + 1);
        await cb?.();
        await client.getUser().then(setUser);
      } finally {
        setPendingUpdates((count) => count - 1);
      }
    },
    [client],
  );

  React.useEffect(() => {
    wrapUpdate();
  }, [wrapUpdate]);

  const login = React.useCallback(
    (connection?: AuthConnection) => wrapUpdate(() => client.login(connection)),
    [client, wrapUpdate],
  );

  const logout = React.useCallback(
    () => wrapUpdate(() => client.logout()),
    [client, wrapUpdate],
  );

  const passwordLogin = React.useCallback(
    (input: PasswordLoginInput) =>
      wrapUpdate(() => client.passwordLogin(input)),
    [client, wrapUpdate],
  );

  const passwordlessStart = React.useCallback(
    (input: PasswordlessStartInput) => client.passwordlessStart(input),
    [client],
  );

  const passwordlessLogin = React.useCallback(
    (input: PasswordlessLoginInput) =>
      wrapUpdate(() => client.passwordlessLogin(input)),
    [client, wrapUpdate],
  );

  const auth = React.useMemo(
    () => ({
      login,
      logout,
      passwordLogin,
      passwordlessStart,
      passwordlessLogin,
      user,
      isAuthenticated: user === undefined ? undefined : user !== null,
      isLoading: user === undefined || Boolean(pendingUpdates),
    }),
    [
      pendingUpdates,
      login,
      logout,
      passwordLogin,
      passwordlessStart,
      passwordlessLogin,
      user,
    ],
  );

  return (
    <KeybanAuthContext.Provider value={auth as AuthContext}>
      {children}
    </KeybanAuthContext.Provider>
  );
}

/**
 * Custom hook to access the Keyban authentication context.
 *
 * This hook provides access to the authentication state and methods
 * managed by the `KeybanProvider`. It must be called within a component
 * that is a descendant of `KeybanProvider`.
 * @returns The Keyban authentication context object.
 * @throws {Error} If the hook is used outside of a `KeybanProvider`.
 * @example
 * ```tsx
 * import React from 'react';
 * import { useKeybanAuth } from './auth'; // Adjust the import path as needed
 *
 * function MyAuthenticatedComponent() {
 *   const { isAuthenticated, user, login, logout } = useKeybanAuth();
 *
 *   if (!isAuthenticated) {
 *     return <button onClick={login}>Log In</button>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.name}!</p>
 *       <button onClick={logout}>Log Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useKeybanAuth() {
  const ctx = React.useContext(KeybanAuthContext);
  if (!ctx)
    throw new Error("useKeybanAuth hook must be used within a KeybanProvider");
  return ctx;
}
